/**
 * Scan-Kamera — öffnet sofort, findet den Beleg live, löst von selbst aus,
 * sobald Beleg und Telefon ruhig sind, und sammelt die Seiten im Stapel.
 *
 * Die Kamera bleibt über den ganzen Scan an (auch während die Übersicht
 * darüber liegt): kein „Kamera startet…" zwischen zwei Seiten, und auf dem
 * iPhone als Home-Bildschirm-App keine erneute Kamera-Freigabe.
 *
 * Aufnahme:
 *  - iPhone: kurze Bildserie, das schärfste Videobild gewinnt (aufnahme.ts).
 *  - Android: echtes Foto in voller Sensorauflösung (ImageCapture.takePhoto).
 * Ist das Ergebnis unscharf, kommt die Seite nicht in den Stapel — die Kamera
 * sagt „ruhig halten" und versucht es erneut. Nach zwei Fehlversuchen wird die
 * Seite trotzdem genommen und in der Übersicht als unscharf markiert (eine
 * Sperre ohne Ausweg macht die App auf mattem Papier unbenutzbar).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { isIOS } from "../../lib/device";
import type { Ecken } from "../../lib/scan/bild";
import {
  FLAECHE_MIN,
  SCHAERFE_MIN,
  abweichung,
  alsJpeg,
  erkenne,
  schaerfeIn,
  serie,
  standardEcken,
  vorschau,
} from "../../lib/scan/aufnahme";
import { MAX_SEITEN, type NeueSeite } from "../../lib/scan/seite";
import { ScanIcon } from "./ScanIcon";
import "./scan.css";

const AUTO_KEY = "scanflow_auto_ausloesen";
/** So lange müssen Beleg und Telefon ruhig sein, bevor automatisch ausgelöst wird. */
const RUHE_MS = 700;
/** Erkennung: so oft je Sekunde (in ms Abstand). */
const TAKT_MS = 140;
/** Ecken dürfen sich zwischen zwei Erkennungen so weit bewegen (Anteil der Diagonale). */
const RUHE_TOLERANZ = 0.018;

type Props = {
  /** false, solange die Übersicht darüber liegt — Kamera bleibt an, Erkennung ruht */
  aktiv: boolean;
  seitenAnzahl: number;
  stapelBild: string | null;
  /** „Wiederholen" aus der Übersicht: genau eine Seite ersetzen */
  ersetzen: boolean;
  onSeite: (s: NeueSeite) => void;
  onFertig: () => void;
  onSchliessen: () => void;
  onDateien: (files: File[]) => void;
  /** meldet die Videoauflösung (Telemetrie) */
  onVideo?: (w: number, h: number) => void;
};

type KameraZustand = "startet" | "laeuft" | "fehlt";

function autoGespeichert(): boolean {
  try {
    return localStorage.getItem(AUTO_KEY) !== "aus";
  } catch {
    return true;
  }
}

export default function ScanKamera({
  aktiv,
  seitenAnzahl,
  stapelBild,
  ersetzen,
  onSeite,
  onFertig,
  onSchliessen,
  onDateien,
  onVideo,
}: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const trackRef = useRef<MediaStreamTrack | null>(null);
  const fotoRef = useRef<any>(null); // ImageCapture (nur Android)
  const fotoEinstellungRef = useRef<any>(undefined);
  const dateiRef = useRef<HTMLInputElement>(null);
  const kameraAppRef = useRef<HTMLInputElement>(null);

  const [kamera, setKamera] = useState<KameraZustand>("startet");
  const [videoMass, setVideoMass] = useState({ w: 0, h: 0 });
  const [erkannt, setErkannt] = useState<{ ecken: Ecken; flaeche: number } | null>(null);
  const [ruhe, setRuhe] = useState(0); // 0..1
  const [auto, setAuto] = useState(autoGespeichert);
  const [blitz, setBlitz] = useState(false);
  const [blitzMoeglich, setBlitzMoeglich] = useState(false);
  const [busy, setBusy] = useState(false);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [fang, setFang] = useState<{ url: string; n: number } | null>(null);
  const [blende, setBlende] = useState(0);

  const busyRef = useRef(false);
  const autoRef = useRef(auto);
  autoRef.current = auto;
  const scharfVersucheRef = useRef(0);
  // Nach einer Aufnahme: erst wieder automatisch auslösen, wenn der Beleg
  // gewechselt wurde (weg aus dem Bild oder deutlich verschoben).
  const scharfGestelltRef = useRef(true);
  const letzteAufnahmeRef = useRef<Ecken | null>(null);
  const zuletztGesehenRef = useRef(0);
  const ruheSeitRef = useRef(0);
  const vorigeEckenRef = useRef<Ecken | null>(null);
  const erkanntRef = useRef<{ ecken: Ecken; flaeche: number } | null>(null);

  const zeigeHinweis = useCallback((t: string) => {
    setHinweis(t);
    window.setTimeout(() => setHinweis((h) => (h === t ? null : h)), 2200);
  }, []);

  // ── Kamera starten / stoppen ──
  const stoppe = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    trackRef.current = null;
    fotoRef.current = null;
  }, []);

  const starte = useCallback(async () => {
    stoppe();
    setKamera("startet");
    const v = videoRef.current;
    if (!v) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      setKamera("fehlt");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: "environment", width: { ideal: 4096 }, height: { ideal: 2160 } },
      });
      streamRef.current = stream;
      const track = stream.getVideoTracks()[0] || null;
      trackRef.current = track;
      try {
        const caps: any = track?.getCapabilities ? track.getCapabilities() : {};
        setBlitzMoeglich(!!caps && "torch" in caps && caps.torch !== false);
        // Android: Dauerfokus, wo das Gerät es anbietet
        if (Array.isArray(caps?.focusMode) && caps.focusMode.includes("continuous")) {
          track.applyConstraints({ advanced: [{ focusMode: "continuous" }] } as any).catch(() => {});
        }
      } catch {
        setBlitzMoeglich(false);
      }
      // Android: echtes Foto statt Videobild. iOS kennt ImageCapture zwar, löst
      // dort aber spürbar verzögert aus — dort bleibt es bei der Bildserie.
      const IC = (window as any).ImageCapture;
      if (track && typeof IC === "function" && !isIOS()) {
        try {
          const ic = new IC(track);
          fotoRef.current = ic;
          ic.getPhotoCapabilities?.()
            .then((c: any) => {
              if (c?.imageWidth?.max && c?.imageHeight?.max) {
                fotoEinstellungRef.current = { imageWidth: c.imageWidth.max, imageHeight: c.imageHeight.max };
              }
            })
            .catch(() => {});
        } catch {
          fotoRef.current = null;
        }
      }
      v.srcObject = stream;
      v.play().catch(() => {});
    } catch (err) {
      console.warn("Kamera:", err);
      setKamera("fehlt");
    }
  }, [stoppe]);

  useEffect(() => {
    starte();
    return () => stoppe();
  }, [starte, stoppe]);

  // Startet die Kamera nicht binnen 8 s, Rückfall anbieten.
  useEffect(() => {
    if (kamera !== "startet") return;
    const t = window.setTimeout(() => setKamera((k) => (k === "startet" ? "fehlt" : k)), 8000);
    return () => window.clearTimeout(t);
  }, [kamera]);

  const beiLaeuft = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    setVideoMass({ w: v.videoWidth, h: v.videoHeight });
    onVideo?.(v.videoWidth, v.videoHeight);
    setKamera("laeuft");
  };

  useEffect(() => {
    const t = trackRef.current;
    if (!t || !blitzMoeglich) return;
    t.applyConstraints({ advanced: [{ torch: blitz }] } as any).catch(() => {});
  }, [blitz, blitzMoeglich]);

  useEffect(() => {
    try {
      localStorage.setItem(AUTO_KEY, auto ? "an" : "aus");
    } catch {
      /* egal */
    }
  }, [auto]);

  // ── Auslösen ──
  const ausloesen = useCallback(async () => {
    const v = videoRef.current;
    if (!v || busyRef.current || kamera !== "laeuft") return;
    if (seitenAnzahl >= MAX_SEITEN && !ersetzen) {
      zeigeHinweis(`Höchstens ${MAX_SEITEN} Seiten je Beleg`);
      return;
    }
    busyRef.current = true;
    setBusy(true);
    setBlende((n) => n + 1);
    try {
      navigator.vibrate?.(20);
    } catch {
      /* iPhone kennt das nicht */
    }
    const live = erkanntRef.current?.ecken ?? null;
    try {
      let quelle: Blob;
      let w: number, h: number;
      let ecken: Ecken | null = null;
      let flaeche = 0;
      let s: number;
      let modus: "serie" | "foto" = "serie";
      let bilder = 1;
      let vorschauQuelle: HTMLCanvasElement | ImageBitmap;
      let foto: Blob | null = null;
      if (fotoRef.current) {
        try {
          foto = await fotoRef.current.takePhoto(fotoEinstellungRef.current);
        } catch {
          foto = null;
        }
      }
      if (foto) {
        const bmp = await createImageBitmap(foto);
        const r = erkenne(bmp);
        ecken = r?.ecken ?? null;
        flaeche = r?.flaeche ?? 0;
        s = schaerfeIn(bmp, ecken);
        w = bmp.width;
        h = bmp.height;
        quelle = foto; // unverändert — keine zusätzliche Kodierung
        modus = "foto";
        vorschauQuelle = bmp;
      } else {
        const erg = await serie(v, live);
        if (istSchwarz(erg.bild)) {
          setKamera("fehlt");
          return;
        }
        const r = erkenne(erg.bild);
        ecken = r?.ecken ?? live;
        flaeche = r?.flaeche ?? erkanntRef.current?.flaeche ?? 0;
        s = r ? schaerfeIn(erg.bild, r.ecken) : erg.schaerfe;
        w = erg.bild.width;
        h = erg.bild.height;
        quelle = await alsJpeg(erg.bild, 0.95);
        bilder = erg.bilder;
        vorschauQuelle = erg.bild;
      }

      const freigeben = () => {
        if (vorschauQuelle instanceof ImageBitmap) vorschauQuelle.close?.();
      };
      if (s < SCHAERFE_MIN && scharfVersucheRef.current < 2) {
        freigeben();
        scharfVersucheRef.current += 1;
        zeigeHinweis("Unscharf — bitte ruhig halten");
        ruheSeitRef.current = performance.now();
        return;
      }
      const unscharf = s < SCHAERFE_MIN;
      scharfVersucheRef.current = 0;
      const erkanntOk = !!ecken;
      const e = ecken ?? standardEcken(w, h);
      const url = await vorschau(vorschauQuelle, e);
      freigeben();

      setFang({ url, n: seitenAnzahl + 1 });
      onSeite({
        quelle,
        w,
        h,
        ecken: e,
        erkannt: erkanntOk,
        unscharf,
        vorschau: url,
        meta: {
          w,
          h,
          sharp: Math.round(s * 100) / 100,
          detected: erkanntOk,
          areaFrac: Math.round(flaeche * 1000) / 1000,
          mode: modus,
          frames: bilder,
          v: 2,
        },
      });
      scharfGestelltRef.current = false;
      letzteAufnahmeRef.current = live;
    } catch (err) {
      console.warn("Aufnahme:", err);
      zeigeHinweis("Aufnahme fehlgeschlagen — bitte noch einmal");
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }, [kamera, seitenAnzahl, ersetzen, onSeite, zeigeHinweis]);

  const ausloesenRef = useRef(ausloesen);
  ausloesenRef.current = ausloesen;

  // ── Live-Erkennung + Ruhe-Messung + Selbstauslöser ──
  useEffect(() => {
    if (!aktiv || kamera !== "laeuft") {
      setRuhe(0);
      if (!aktiv) setFang(null);
      return;
    }
    let aus = false;
    let zuletzt = 0;
    let raf = 0;
    const takt = (ts: number) => {
      if (aus) return;
      raf = requestAnimationFrame(takt);
      if (busyRef.current || ts - zuletzt < TAKT_MS) return;
      zuletzt = ts;
      const v = videoRef.current;
      if (!v || v.readyState < 2 || !v.videoWidth) return;
      let r: { ecken: Ecken; flaeche: number } | null = null;
      try {
        r = erkenne(v);
      } catch {
        r = null;
      }
      const jetzt = performance.now();
      const w = v.videoWidth, h = v.videoHeight;
      if (r) zuletztGesehenRef.current = jetzt;
      erkanntRef.current = r;
      setErkannt(r);

      // Neu scharf stellen, sobald der vorige Beleg weg oder deutlich bewegt ist
      if (!scharfGestelltRef.current) {
        const weg = jetzt - zuletztGesehenRef.current > 700;
        const vorige = letzteAufnahmeRef.current;
        const bewegt = !vorige || (!!r && abweichung(r.ecken, vorige, w, h) > 0.12);
        if (weg || bewegt) scharfGestelltRef.current = true;
      }

      const gut = !!r && r.flaeche >= FLAECHE_MIN;
      const vorher = vorigeEckenRef.current;
      vorigeEckenRef.current = r?.ecken ?? null;
      if (!gut || !vorher || abweichung(r!.ecken, vorher, w, h) > RUHE_TOLERANZ) {
        ruheSeitRef.current = jetzt;
        setRuhe(0);
        return;
      }
      const p = Math.min(1, (jetzt - ruheSeitRef.current) / RUHE_MS);
      setRuhe(p);
      if (p >= 1 && autoRef.current && scharfGestelltRef.current) {
        ruheSeitRef.current = jetzt;
        void ausloesenRef.current();
      }
    };
    raf = requestAnimationFrame(takt);
    return () => {
      aus = true;
      cancelAnimationFrame(raf);
    };
  }, [aktiv, kamera]);

  const dateienGewaehlt = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (f.length) onDateien(f);
  };

  const gut = !!erkannt && erkannt.flaeche >= FLAECHE_MIN;
  let status: { text: string; art: "gut" | "mitte" | "schwach" };
  if (hinweis) status = { text: hinweis, art: "schwach" };
  else if (busy) status = { text: "Ruhig halten …", art: "mitte" };
  else if (!erkannt) status = { text: "Beleg ins Bild halten", art: "mitte" };
  else if (!gut) status = { text: "Näher heran", art: "schwach" };
  else if (auto && !scharfGestelltRef.current) status = { text: "Nächste Seite hinlegen", art: "mitte" };
  else if (auto) status = { text: "Ruhig halten …", art: "gut" };
  else status = { text: "Beleg erkannt", art: "gut" };

  const RING = 2 * Math.PI * 41;
  const zeigeRing = auto && gut && scharfGestelltRef.current && !busy;

  return (
    <div className={"sc-kamera" + (aktiv ? "" : " is-ruht")}>
      <video
        ref={videoRef}
        className="sc-video"
        playsInline
        muted
        autoPlay
        onPlaying={beiLaeuft}
        onLoadedMetadata={() => videoRef.current?.play().catch(() => {})}
      />

      {kamera === "laeuft" && erkannt && videoMass.w > 0 && (
        <svg
          className="sc-rahmen"
          viewBox={`0 0 ${videoMass.w} ${videoMass.h}`}
          preserveAspectRatio="xMidYMid slice"
        >
          <polygon
            className={gut ? "is-gut" : "is-schwach"}
            points={[erkannt.ecken.tl, erkannt.ecken.tr, erkannt.ecken.br, erkannt.ecken.bl]
              .map((p) => `${p.x},${p.y}`)
              .join(" ")}
            strokeWidth={Math.max(3, Math.min(videoMass.w, videoMass.h) * 0.006)}
          />
        </svg>
      )}

      {blende > 0 && <div key={blende} className="sc-blende" />}

      <input
        ref={dateiRef}
        type="file"
        accept="image/*,application/pdf"
        multiple
        hidden
        onChange={dateienGewaehlt}
      />
      <input
        ref={kameraAppRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={dateienGewaehlt}
      />

      {kamera === "fehlt" && (
        <div className="sc-fehlt">
          <div className="sc-fehlt-ic">
            <ScanIcon name="kamera" />
          </div>
          <h2>Kamera lässt sich hier nicht starten</h2>
          <p>Fotografiere mit der Kamera-App des Telefons oder wähle ein Foto bzw. PDF aus.</p>
          <button className="sc-knopf sc-knopf-primaer" onClick={() => kameraAppRef.current?.click()}>
            Mit Kamera-App fotografieren
          </button>
          <button className="sc-knopf" onClick={() => dateiRef.current?.click()}>
            Foto oder PDF wählen
          </button>
          <button className="sc-knopf sc-knopf-leise" onClick={() => starte()}>
            Kamera erneut starten
          </button>
        </div>
      )}

      <div className="sc-oben">
        <button className="sc-glas" onClick={onSchliessen} aria-label="Schließen">
          <ScanIcon name="x" />
        </button>
        <div className="sc-titel">{ersetzen ? "Seite neu aufnehmen" : seitenAnzahl > 0 ? `Seite ${seitenAnzahl + 1}` : "Beleg scannen"}</div>
        <div className="sc-oben-rechts">
          {blitzMoeglich && (
            <button
              className={"sc-glas" + (blitz ? " is-an" : "")}
              onClick={() => setBlitz((b) => !b)}
              aria-label={blitz ? "Licht aus" : "Licht an"}
            >
              <ScanIcon name={blitz ? "blitz" : "blitzAus"} />
            </button>
          )}
          <button
            className={"sc-auto" + (auto ? " is-an" : "")}
            onClick={() => setAuto((a) => !a)}
            aria-label="Automatisch auslösen"
          >
            {auto ? "Auto" : "Manuell"}
          </button>
        </div>
      </div>

      {kamera === "laeuft" && aktiv && (
        <div className={"sc-status is-" + status.art}>{status.text}</div>
      )}

      {fang && (
        <img
          key={fang.n}
          className="sc-fang"
          src={fang.url}
          alt=""
          onAnimationEnd={() => setFang(null)}
        />
      )}

      <div className="sc-unten">
        <div className="sc-seite">
          {seitenAnzahl > 0 && stapelBild ? (
            <button className="sc-stapel" onClick={onFertig} aria-label="Seiten ansehen">
              <img src={stapelBild} alt="" />
              <span className="sc-zahl">{seitenAnzahl}</span>
            </button>
          ) : (
            <span className="sc-platz" />
          )}
        </div>

        <button
          className={"sc-ausloeser" + (kamera === "laeuft" && !busy ? "" : " is-aus")}
          onClick={() => void ausloesen()}
          aria-label="Auslösen"
        >
          {zeigeRing && (
            <svg className="sc-ring" viewBox="0 0 88 88">
              <circle cx="44" cy="44" r="41" strokeDasharray={RING} strokeDashoffset={RING * (1 - ruhe)} />
            </svg>
          )}
        </button>

        <div className="sc-seite">
          {seitenAnzahl > 0 && !ersetzen ? (
            <button className="sc-fertig" onClick={onFertig}>
              Fertig
            </button>
          ) : (
            <button className="sc-waehlen" onClick={() => dateiRef.current?.click()}>
              <ScanIcon name="galerie" />
              <span>Wählen</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** Liefert die Kamera nur Schwarz? (iPhone-Eigenheit bei manchen Starts) */
function istSchwarz(c: HTMLCanvasElement): boolean {
  const k = document.createElement("canvas");
  k.width = k.height = 16;
  const ctx = k.getContext("2d", { willReadFrequently: true });
  if (!ctx) return false;
  ctx.drawImage(c, 0, 0, 16, 16);
  const d = ctx.getImageData(0, 0, 16, 16).data;
  let s = 0;
  for (let i = 0; i < d.length; i += 4) s += d[i] + d[i + 1] + d[i + 2];
  return s / (d.length / 4) / 3 < 6;
}
